// Megabonk-style powerup pool. On level-up the server rolls 3 cards via
// rarity weights, the client shows them, the player picks one, and both
// sides converge on the chosen id. Effects are declarative: the server
// applies combat stats (hp, damage, absorption, xp mult), the client
// applies movement feel (speed mult) locally since the controller is
// client-authoritative.

import type { Role } from '../net/protocol';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export type PowerupCategory = 'stat' | 'body' | 'equipment';

export type PowerupEffect = {
  hpMaxAdd?: number;
  damageMult?: number;
  absorptionMult?: number;
  xpMult?: number;
  speedMult?: number;
  circleRadiusMult?: number;   // chad only
  ringOuterMult?: number;      // virgin only
  hitboxShrink?: number;       // virgin body mod — 0..1 (0.2 = 20% smaller)
};

export type Powerup = {
  id: string;
  name: string;
  blurb: string;
  rarity: Rarity;
  category: PowerupCategory;
  faction?: Role;              // if set, only appears for that faction
  oneTime?: boolean;           // body mods — cannot stack
  effect: PowerupEffect;
};

// Every powerup is now oneTime — duplicates are filtered out on
// roll + granted. Effects from DIFFERENT powerups still stack
// multiplicatively (e.g. protein × pre-workout × tumbler combine
// damageMult), so the progression system still rewards picking up
// variety. Body mods (radius/hitbox) were already oneTime.
export const POWERUPS: ReadonlyArray<Powerup> = [
  // ---- Common (55%) ----
  { id: 'energizer',   name: 'Energizer',        blurb: '+10% Movement Speed',         rarity: 'common',    category: 'stat', oneTime: true, effect: { speedMult: 1.10 } },
  { id: 'gym_bro',     name: 'Gym Bro',          blurb: '+10 Max HP',              rarity: 'common',    category: 'stat', oneTime: true, effect: { hpMaxAdd: 10 } },
  { id: 'protein',     name: 'Protein Shake',    blurb: '+8% Damage',              rarity: 'common',    category: 'stat', oneTime: true, effect: { damageMult: 1.08 } },
  { id: 'late_night',  name: 'Late Night Grind', blurb: '+10% XP gain',            rarity: 'common',    category: 'stat', oneTime: true, effect: { xpMult: 1.10 } },
  { id: 'caffeine',    name: 'Caffeine',         blurb: '+8% Movement Speed, +5% XP',  rarity: 'common',    category: 'stat', oneTime: true, effect: { speedMult: 1.08, xpMult: 1.05 } },
  { id: 'meal_prep',   name: 'Meal Prep',        blurb: '+15 Max HP',              rarity: 'common',    category: 'stat', oneTime: true, effect: { hpMaxAdd: 15 } },
  { id: 'finger_guns', name: 'Finger Guns',      blurb: '+10% NPC Absorption',     rarity: 'common',    category: 'stat', oneTime: true, effect: { absorptionMult: 1.10 } },

  // ---- Uncommon (25%) ----
  { id: 'podcast',       name: 'Podcast',          blurb: '+20% XP gain',          rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { xpMult: 1.20 } },
  { id: 'deep_breath',   name: 'Deep Breath',      blurb: '+25 Max HP',            rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { hpMaxAdd: 25 } },
  { id: 'zen_garden',    name: 'Zen Garden',       blurb: '+18% NPC Absorption',   rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { absorptionMult: 1.18 } },
  { id: 'pre_workout',   name: 'Pre-Workout',      blurb: '+15% Damage',           rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { damageMult: 1.15 } },
  { id: 'noise_cancel',  name: 'Noise Cancelling', blurb: '+18% Movement Speed',       rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { speedMult: 1.18 } },
  { id: 'lean_build',    name: 'Lean Build',       blurb: 'Smaller hitbox',        rarity: 'uncommon',  category: 'body', faction: 'int', oneTime: true, effect: { hitboxShrink: 0.20 } },
  { id: 'big_energy',    name: 'Big Energy',       blurb: '+25% Circle radius',    rarity: 'uncommon',  category: 'body', faction: 'ext', oneTime: true, effect: { circleRadiusMult: 1.25 } },

  // ---- Rare (12%) ----
  { id: 'pro_gamer',     name: 'Pro Gamer',        blurb: '+25% NPC Absorption, +5% Movement Speed', rarity: 'rare',      category: 'stat', oneTime: true, effect: { absorptionMult: 1.25, speedMult: 1.05 } },
  { id: 'lifetime_sub',  name: 'Lifetime Sub',     blurb: '+40 Max HP',            rarity: 'rare',      category: 'stat', oneTime: true, effect: { hpMaxAdd: 40 } },
  { id: 'tumbler',       name: 'Insulated Tumbler',blurb: '+25% Damage',           rarity: 'rare',      category: 'stat', oneTime: true, effect: { damageMult: 1.25 } },
  { id: 'sprinter',      name: "Sprinter's Legs",  blurb: '+30% Movement Speed',       rarity: 'rare',      category: 'stat', oneTime: true, effect: { speedMult: 1.30 } },
  { id: 'extended_ring', name: 'Extended Ring',    blurb: '+20% Outer ring',       rarity: 'rare',      category: 'body', faction: 'int', oneTime: true, effect: { ringOuterMult: 1.20 } },
  { id: 'mega_circle',   name: 'Mega Circle',      blurb: '+40% Circle radius',    rarity: 'rare',      category: 'body', faction: 'ext', oneTime: true, effect: { circleRadiusMult: 1.40 } },

  // ---- Epic (6%) ----
  { id: 'main_char',     name: 'Main Character',   blurb: '+30% Damage, +20% XP',  rarity: 'epic',      category: 'stat', oneTime: true, effect: { damageMult: 1.30, xpMult: 1.20 } },
  { id: 'protein_keg',   name: 'Protein Keg',      blurb: '+60 Max HP, +10% Dmg',  rarity: 'epic',      category: 'stat', oneTime: true, effect: { hpMaxAdd: 60, damageMult: 1.10 } },

  // ---- Legendary (2%) ----
  { id: 'viral_moment',  name: 'Viral Moment',     blurb: '×2 XP gain permanently',rarity: 'legendary', category: 'stat', oneTime: true, effect: { xpMult: 2.0 } },
  { id: 'transcendence', name: 'Transcendence',    blurb: '+50 HP, +25% Dmg, +15% Movement Speed', rarity: 'legendary', category: 'stat', oneTime: true, effect: { hpMaxAdd: 50, damageMult: 1.25, speedMult: 1.15 } },
];

const RARITY_WEIGHT: Record<Rarity, number> = {
  common: 55,
  uncommon: 25,
  rare: 12,
  epic: 6,
  legendary: 2,
};

export const RARITY_COLOR: Record<Rarity, string> = {
  common:    '#9ca3af',
  uncommon:  '#22c55e',
  rare:      '#3b82f6',
  epic:      '#a855f7',
  legendary: '#f59e0b',
};

export function getPowerup(id: string): Powerup | null {
  return POWERUPS.find((p) => p.id === id) ?? null;
}

// Rarity-weighted random roll, restricted to cards the player is allowed
// to see. Cards already owned (for oneTime body mods) are filtered out.
// Rolls 3 unique cards.
export function rollThree(role: Role, ownedOneTime: ReadonlySet<string>): Powerup[] {
  const eligible = POWERUPS.filter((p) => {
    if (p.faction && p.faction !== role) return false;
    if (p.oneTime && ownedOneTime.has(p.id)) return false;
    return true;
  });
  if (eligible.length === 0) return [];

  const picks: Powerup[] = [];
  const remaining = eligible.slice();
  for (let i = 0; i < 3 && remaining.length > 0; i++) {
    const totalWeight = remaining.reduce((s, p) => s + RARITY_WEIGHT[p.rarity], 0);
    let roll = Math.random() * totalWeight;
    let idx = 0;
    for (let j = 0; j < remaining.length; j++) {
      roll -= RARITY_WEIGHT[remaining[j].rarity];
      if (roll <= 0) { idx = j; break; }
    }
    picks.push(remaining[idx]);
    remaining.splice(idx, 1);
  }
  return picks;
}

// Combined effect of a stack of powerups. Starts from a neutral identity
// and folds all additive/multiplicative fields. Used by both the server
// (HP max / damage / absorption / XP) and the client (speed + body).
export type DerivedStats = {
  hpMaxAdd: number;
  damageMult: number;
  absorptionMult: number;
  xpMult: number;
  speedMult: number;
  circleRadiusMult: number;
  ringOuterMult: number;
  hitboxShrink: number;
};

export function neutralStats(): DerivedStats {
  return {
    hpMaxAdd: 0,
    damageMult: 1,
    absorptionMult: 1,
    xpMult: 1,
    speedMult: 1,
    circleRadiusMult: 1,
    ringOuterMult: 1,
    hitboxShrink: 0,
  };
}

export function deriveStats(
  stack: ReadonlyArray<string>,
  extra?: PowerupEffect,
): DerivedStats {
  const s = neutralStats();
  const apply = (e: PowerupEffect) => {
    if (e.hpMaxAdd)         s.hpMaxAdd += e.hpMaxAdd;
    if (e.damageMult)       s.damageMult *= e.damageMult;
    if (e.absorptionMult)   s.absorptionMult *= e.absorptionMult;
    if (e.xpMult)           s.xpMult *= e.xpMult;
    if (e.speedMult)        s.speedMult *= e.speedMult;
    if (e.circleRadiusMult) s.circleRadiusMult *= e.circleRadiusMult;
    if (e.ringOuterMult)    s.ringOuterMult *= e.ringOuterMult;
    if (e.hitboxShrink)     s.hitboxShrink = Math.max(s.hitboxShrink, e.hitboxShrink);
  };
  for (const id of stack) {
    const p = getPowerup(id);
    if (p) apply(p.effect);
  }
  if (extra) apply(extra);
  return s;
}
